// 지역 안내 카드의 반응형 배치(순수 로직, DOM 없음). 글자를 transform으로 줄이지 않고, 화면에 맞춰 카드 너비·간격·표시 줄을 바꾼다.
// 어느 배치에서도 본문 글자는 실제 화면에서 12px 이상이다. 카드는 위쪽 HUD(미니맵 아래 끝) 밑, 화면 오른쪽 아래에 둔다.

/** 배치 이름. 앞쪽일수록 넉넉하고 높이가 크다 */
export type CardMode = 'full' | 'wide' | 'compact' | 'tight' | 'minimal' | 'band';
export const CARD_MODES: readonly CardMode[] = ['full', 'wide', 'compact', 'tight', 'minimal', 'band'];
/**
 * 카드는 화면 오른쪽 가장자리 띠에만 둔다(PR #160 리뷰): 왼쪽 끝이 게임 화면 너비의 이 비율보다 오른쪽 — 가운데 영웅·앞길과
 * 아래 가운데 「자동 이동 중」 알약에 닿지 않게. 좁은 화면에서 줄이 넘치면 마지막 배치(band)는 이름·스테이지 줄·안내만 남긴다
 */
export const CARD_EDGE_FRAC = 0.67;

/** 미니맵 아래 끝(디자인 px): HUD 우상단 묶음의 위쪽 6 + 높이 약 215 */
export const TOP_HUD_BOTTOM = 6;
export const TOP_HUD_HEIGHT = 215;
/** 카드와 HUD 사이 틈, 카드 아래·오른쪽 여백(CSS px) */
export const CARD_GAP_TOP = 8;
export const CARD_MARGIN_BOTTOM = 12;
export const CARD_MARGIN_RIGHT = 8;
/** 기본 너비와 가로로 넓힌 배치의 최대 너비(CSS px) */
export const CARD_W_FULL = 262;
export const CARD_W_WIDE = 420;
/** 카드 본문의 실제 화면 최소 글자 크기(CSS px) */
export const CARD_MIN_FONT = 12;
/** 큰 화면에서만 키운다(작게 줄이지 않는다) */
export const CARD_MAX_ZOOM = 1.5;

export interface CardStyle {
  readonly width: number | 'wide';
  readonly pad: string;
  readonly nameSize: number;
  readonly blurbSize: number;
  readonly bodySize: number;
  readonly rowGap: number;
  readonly rowsTop: number;
  readonly hintTop: number;
  readonly hintPad: number;
  /** 한 줄 소개 표시 */
  readonly blurb: boolean;
  /** 주요 적·보스 줄 표시 */
  readonly extra: boolean;
  /** 위험·권장·보상·내 기록 줄 표시(가장자리 띠에 안 들어가는 좁은 화면의 마지막 배치만 숨김) */
  readonly rows: boolean;
}

/** 모든 글자 크기는 CARD_MIN_FONT 이상 */
export const CARD_STYLE: Readonly<Record<CardMode, CardStyle>> = {
  full:    { width: CARD_W_FULL, pad: '10px 12px 11px', nameSize: 19, blurbSize: 13.5, bodySize: 13, rowGap: 4, rowsTop: 7, hintTop: 8, hintPad: 7, blurb: true, extra: true, rows: true },
  wide:    { width: 'wide',      pad: '10px 12px 11px', nameSize: 19, blurbSize: 13.5, bodySize: 13, rowGap: 4, rowsTop: 7, hintTop: 8, hintPad: 7, blurb: true, extra: true, rows: true },
  compact: { width: 'wide',      pad: '8px 12px 8px',   nameSize: 17, blurbSize: 13,   bodySize: 12.5, rowGap: 2, rowsTop: 5, hintTop: 5, hintPad: 5, blurb: true, extra: true, rows: true },
  tight:   { width: 'wide',      pad: '8px 12px 8px',   nameSize: 17, blurbSize: 13,   bodySize: 12.5, rowGap: 2, rowsTop: 4, hintTop: 4, hintPad: 4, blurb: false, extra: true, rows: true },
  minimal: { width: 'wide',      pad: '7px 12px 7px',   nameSize: 17, blurbSize: 13,   bodySize: 12.5, rowGap: 1, rowsTop: 3, hintTop: 3, hintPad: 3, blurb: false, extra: false, rows: true },
  band:    { width: 'wide',      pad: '6px 10px 6px',   nameSize: 16, blurbSize: 13,   bodySize: 12.5, rowGap: 1, rowsTop: 2, hintTop: 3, hintPad: 3, blurb: false, extra: false, rows: false },
};

export interface CardBounds {
  /** 큰 화면에서만 1 초과(transform 확대) */
  readonly zoom: number;
  /** 확대 전 CSS px 기준으로 카드가 쓸 수 있는 높이·너비 */
  readonly availH: number;
  readonly availW: number;
}

/** 화면(W×H CSS px)과 디자인 배율 u로 카드가 들어갈 영역을 구한다. 위쪽 HUD(미니맵)는 u배로 그려진다 */
export function cardBounds(W: number, H: number, u: number): CardBounds {
  const zoom = Math.max(1, Math.min(u, CARD_MAX_ZOOM));
  const top = (TOP_HUD_BOTTOM + TOP_HUD_HEIGHT) * u + CARD_GAP_TOP;
  return {
    zoom,
    availH: Math.max(0, (H - CARD_MARGIN_BOTTOM - top) / zoom),
    availW: Math.max(0, (W * (1 - CARD_EDGE_FRAC) - CARD_MARGIN_RIGHT) / zoom),
  };
}

/** 배치의 카드 너비(CSS px). 화면이 좁으면 화면 안으로 줄인다 */
export function cardWidth(mode: CardMode, availW: number): number {
  const w = CARD_STYLE[mode].width === 'wide' ? CARD_W_WIDE : CARD_W_FULL;
  return Math.max(0, Math.min(w, availW));
}

/** 앞 배치부터 차례로 재어 높이가 availH 안에 드는 첫 배치를 고른다. 모두 넘치면 마지막(가장 작은) 배치 */
export function pickCardMode(availH: number, heightOf: (mode: CardMode) => number): CardMode {
  for (const m of CARD_MODES) if (heightOf(m) <= availH) return m;
  return CARD_MODES[CARD_MODES.length - 1];
}
